import type { TWasmMapHover } from "../wasm";
import type CursorManager from "~/util/cursorManager";
import type Wasm from "../wasm";
import { createMarkerPopup } from "~/ui/map/markerpopup";
import { computePosition, shift } from "@floating-ui/dom";
import { nearestPointOnLine } from "@turf/nearest-point-on-line";
import { lineString, point } from "@turf/helpers";
import { cleanCoords } from "@turf/clean-coords";
import { length } from "@turf/length";
import { stringToCoords } from "~/util/mirek";
import { standardDistanceFormat } from "~/util/format";
import { createRouteSummaryPopup } from "~/ui/map/routesummarypopup";
import { IXYPosition } from "~/interfaces";
import { IWasmStoreGeometry, IWasmStoreMarker } from "../store/interfaces";
import { setImportPopupContent, setMMPopupContent, setPOIPopupContent, setSharedLocationPopupContent } from "./popupcontent";
import { openDIM } from "~/util/userweb";
import { CONFIG } from "~/config";

const SHOW_POPUP_DELAY = 500;
const HIDE_POPUP_DELAY = 250;
/** DOM element pouzivany pro popup nad markerem v mape */
const markerPopup = createMarkerPopup();
/** timeout pro zobrazeni popupu */
let popupShowTimeout: number = 0;
/** timeout pro schovani popupu */
let popupHideTimeout: number = 0;
let wasmIns: Wasm = null;
let tempPos: IXYPosition = null;

export function setWasmIns(newWasmIns: Wasm) {
	wasmIns = newWasmIns;
}

export function isPopupConnected() {
	return markerPopup.container.isConnected;
}

export function updatePopupPosition(clientX: number, clientY: number) {
	if (isPopupConnected()) {
		computePosition({
			getBoundingClientRect() {
				return {
					width: 0,
					height: 0,
					x: clientX,
					y: clientY,
					top: clientY,
					left: clientX,
					right: clientX,
					bottom: clientY,
				};
			},
		}, markerPopup.container, {
			placement: "bottom-start",
			middleware: [
				shift({
					mainAxis: true,
					crossAxis: true,
					padding: {
						top: 0,
						// pocitame jen s uzkym sloupcem nebo zadnym, pro siroky nechame popupy pretekat i do nej
						right: Mapy.getComponent("layout").getWidth() === 1 ? 400 : 0,
						bottom: 0,
						left: 0,
					},
				}),
			],
		}).then(({ x, y }) => {
			Object.assign(markerPopup.container.style, {
				left: `${x}px`,
				top: `${y}px`,
			});
		});
	} else {
		tempPos = {
			x: clientX,
			y: clientY,
		};
	}
}

export function showPopup({ clientX, clientY, contentBuilder, mode = "marker" }: { clientX: number, clientY: number, contentBuilder: () => Promise<DocumentFragment>, mode: "marker" | "geometry" }) {
	// pokud se melo ukazat neco jineho, tak ted uz ne
	popupShowTimeout && clearTimeout(popupShowTimeout);
	// pokud se melo schovavat, tak ted uz ne
	popupHideTimeout && clearTimeout(popupHideTimeout);
	popupHideTimeout = 0;
	// nastavime timeout na zobrazeni popupu
	popupShowTimeout = window.setTimeout(async () => {
		// pokud ve chvili zobrazeni mame neco schovat, tak to schovani zrusime
		popupHideTimeout && clearTimeout(popupHideTimeout);
		// vynulujeme si timeouty
		popupShowTimeout = 0;
		popupHideTimeout = 0;
		// skutecne ukazeme popup
		markerPopup.card.innerHTML = "";
		markerPopup.card.appendChild(await contentBuilder());
		markerPopup.container.classList[mode === "geometry" ? "add" : "remove"]("ui-marker-popup--geometry");
		document.body.appendChild(markerPopup.container);

		if (tempPos) {
			updatePopupPosition(tempPos.x, tempPos.y);
			tempPos = null;
		} else {
			updatePopupPosition(clientX, clientY);
		}

		// navesime ruzne posluchace pro schovani popupu
		markerPopup.card.addEventListener("click", () => {
			// eslint-disable-next-line @typescript-eslint/no-use-before-define
			hidePopup(true);
		}, { once: true });
		document.querySelector("canvas.wasm-canvas")?.addEventListener("pointerdown", () => {
			// eslint-disable-next-line @typescript-eslint/no-use-before-define
			hidePopup(true);
		}, { once: true });
		window.addEventListener("resize", () => {
			// eslint-disable-next-line @typescript-eslint/no-use-before-define
			hidePopup(true);
		}, { once: true });
		window.addEventListener("wheel", () => {
			// eslint-disable-next-line @typescript-eslint/no-use-before-define
			hidePopup(true);
		}, { once: true });
	}, SHOW_POPUP_DELAY);
}

export function hidePopup(instant = false) {
	wasmIns?.unhoverMarker();

	if (isPopupConnected()) {
		!popupHideTimeout && (popupHideTimeout = window.setTimeout(() => {
			// pokud se melo ukazat neco jineho, tak ted uz ne
			popupShowTimeout && clearTimeout(popupShowTimeout);
			popupShowTimeout = 0;
			// skutecne schovame popup
			markerPopup.container.remove();
		}, instant ? 0 : HIDE_POPUP_DELAY));
	} else {
		popupShowTimeout && clearTimeout(popupShowTimeout);
		popupShowTimeout = 0;
	}
}

markerPopup.card.addEventListener("pointerenter", () => {
	popupHideTimeout && clearTimeout(popupHideTimeout);
	popupHideTimeout = 0;
	popupShowTimeout && clearTimeout(popupShowTimeout);
	popupShowTimeout = 0;
});
markerPopup.card.addEventListener("pointerleave", () => {
	hidePopup();
});

/** reference na popup pro shrnuti trasy */
let summaryGeometryPopup: ReturnType<typeof createRouteSummaryPopup> = null;
let couldBeOpen = false;

function countLengths(strPoints: IWasmStoreGeometry["strPoints"], index: number, contactPoint: IXYPosition) {
	// zatim jsem jeste nevidel data, kde by pole v poli melo vic nez jeden prvek, ale pro jistotu to tady osetrime
	const usableStrPoints = strPoints.map((item: string | Array<string>) => Array.isArray(item) && item.length ? item[0] : item.toString());

	// mame jednu jedinou geometrii
	if (usableStrPoints.length === 1) {
		/** reference na GEOJson linii */
		const summaryGeometryLine = cleanCoords(lineString(stringToCoords(usableStrPoints[0])));
		/** celkova delka GEOJson geometrie */
		const summaryGeometryTotalLength = length(summaryGeometryLine, { units: "meters" });
		const nearest = nearestPointOnLine(summaryGeometryLine, point([contactPoint.x, contactPoint.y]), { units: "meters" });

		return [standardDistanceFormat(nearest.properties.location.toString()), standardDistanceFormat(((summaryGeometryTotalLength - nearest.properties.location)).toString())];
	}

	// mame vice geometrii
	let fromStart = 0;
	let toEnd = 0;

	usableStrPoints.forEach((geometry, geometryIndex) => {
		if (geometryIndex < index) {
			const geometryLine = cleanCoords(lineString(stringToCoords(geometry)));

			fromStart += length(geometryLine, { units: "meters" });
		} else if (geometryIndex > index) {
			const geometryLine = cleanCoords(lineString(stringToCoords(geometry)));

			toEnd += length(geometryLine, { units: "meters" });
		} else {
			// aktualni geometrie, nad kterou jsme
			const geometryLine = cleanCoords(lineString(stringToCoords(geometry)));
			const nearest = nearestPointOnLine(geometryLine, point([contactPoint.x, contactPoint.y]), { units: "meters" });

			fromStart += nearest.properties.location;
			toEnd += length(geometryLine, { units: "meters" }) - nearest.properties.location;
		}
	});

	return [standardDistanceFormat(fromStart.toString()), standardDistanceFormat(toEnd.toString())];
}

export async function setRouteSummaryPopup(strPoints: IWasmStoreGeometry["strPoints"], index: number, contactX: number, contactY: number, wasm: Wasm) {
	couldBeOpen = true;

	const contactPoint = await wasm.coordsFromScreenPosition(contactX, contactY);

	if (!couldBeOpen) {
		return;
	}

	const [fromObject, toObject] = countLengths(strPoints, index, contactPoint);

	// pri prvnim zobrazeni vytvorime popup
	if (!summaryGeometryPopup) {
		summaryGeometryPopup = createRouteSummaryPopup();
	}

	summaryGeometryPopup.fromStart.innerHTML = `<strong>${fromObject.distance}</strong>&nbsp;${fromObject.unit}`;
	summaryGeometryPopup.toFinish.innerHTML = `<strong>${toObject.distance}</strong>&nbsp;${toObject.unit}`;
	summaryGeometryPopup.container.style.setProperty("left", `${contactX}px`);
	summaryGeometryPopup.container.style.setProperty("bottom", `${window.innerHeight - contactY}px`);
	document.body.appendChild(summaryGeometryPopup.container);
}

export function unsetRouteSummaryPopup() {
	summaryGeometryPopup?.container.remove();
	couldBeOpen = false;
}

/** reference na aktualni kurzor */
let hoverCursor: object = null;

export function useCursor(cursor: string, data: TWasmMapHover, cursorManager: CursorManager) {
	if (data.type === "enter") {
		if (hoverCursor) {
			cursorManager.update(hoverCursor, cursor);
		} else {
			hoverCursor = cursorManager.add(cursor);
		}
	} else if (data.type === "leave") {
		hoverCursor && cursorManager.remove(hoverCursor);
		hoverCursor = null;
	}
}

export function isRouteActive() {
	return Mapy.getComponent("route").isActive() && !(Mapy.getComponent("distanceMeter").isActive() || Mapy.getComponent("userMarks").isActive() || Mapy.getComponent("search").isActive());
}

/* popupy u markeru */

export async function shouldDisplayPopup(wasmMarker: IWasmStoreMarker, wasm: Wasm): Promise<boolean> {
	const visibilityInfo = await wasm.mapContent.getElementsVisibilityInfo([wasmMarker.id], 0, 0, 0, 0);

	return visibilityInfo.visibleCount > 0 || visibilityInfo.partiallyVisibleCount > 0;
}

export async function popupSearch(wasmMarker: IWasmStoreMarker, wasm: Wasm) {
	const shouldDisplay = await shouldDisplayPopup(wasmMarker, wasm);

	if (!shouldDisplay) {
		return;
	}

	const anchor = await wasm.coordsToPixel({
		x: wasmMarker.lon,
		y: wasmMarker.lat,
	});
	const clientX = anchor.x - (wasmMarker.type === "big" || wasmMarker.type === "big-universal" ? 16 : 8);
	const clientY = anchor.y - 8;

	showPopup({
		clientX,
		clientY,
		contentBuilder: () => setPOIPopupContent(wasmMarker.customData.poi.source, wasmMarker.customData.poi.id, true),
		mode: "marker",
	});
}

export async function popupMM(wasmMarker: IWasmStoreMarker, wasm: Wasm) {
	const shouldDisplay = await shouldDisplayPopup(wasmMarker, wasm);

	if (!shouldDisplay) {
		return;
	}

	const anchor = await wasm.coordsToPixel({
		x: wasmMarker.lon,
		y: wasmMarker.lat,
	});
	const clientX = anchor.x - 8;
	const clientY = anchor.y - 8;

	showPopup({
		clientX,
		clientY,
		contentBuilder: () => setMMPopupContent(wasmMarker.customData.mmItem, false, (_event, item) => {
			openDIM(item.id, { inFrame: CONFIG.inFrame }).then(() => {
				if (!CONFIG.inFrame) {
					Mapy.getComponent("history").save();
				}
			}, () => {});
		}),
		mode: "marker",
	});
}

export async function popupAlwaysOnMap(wasmMarker: IWasmStoreMarker, wasm: Wasm) {
	const anchor = await wasm.coordsToPixel({
		x: wasmMarker.lon,
		y: wasmMarker.lat,
	});
	const clientX = anchor.x - 2;
	const clientY = anchor.y + 10;

	showPopup({
		clientX,
		clientY,
		contentBuilder: () => setMMPopupContent(wasmMarker.customData.mmItem, true, (_event, item) => {
			openDIM(item.id, { inFrame: CONFIG.inFrame }).then(() => {
				if (!CONFIG.inFrame) {
					Mapy.getComponent("history").save();
				}
			}, () => {});
		}),
		mode: "marker",
	});
}

export async function popupSharePosition(wasmMarker: IWasmStoreMarker, wasm: Wasm) {
	const shouldDisplay = await shouldDisplayPopup(wasmMarker, wasm);

	if (!shouldDisplay) {
		return;
	}

	const anchor = await wasm.coordsToPixel({
		x: wasmMarker.lon,
		y: wasmMarker.lat,
	});
	const clientX = anchor.x - 12;
	const clientY = anchor.y + 12;

	showPopup({
		clientX,
		clientY,
		contentBuilder: () => setSharedLocationPopupContent(wasmMarker.customData.subscriptionId),
		mode: "marker",
	});
}

export async function popupImport(wasmMarker: IWasmStoreMarker, wasmGeometry: IWasmStoreGeometry, wasm: Wasm, contact: IXYPosition) {
	const wasmEntity = wasmMarker || wasmGeometry;
	const customId: string = wasmEntity.customData?.customId as string;
	const title: string = wasmEntity.customData?.title as string;
	const description: string = wasmEntity.customData?.description as string;
	const anchor = wasmMarker
		? await wasm.coordsToPixel({
			x: wasmMarker.lon,
			y: wasmMarker.lat,
		})
		: wasmGeometry
			? {
				x: contact.x,
				y: contact.y,
			}
			: null;

	if (anchor) {
		const clientX = anchor.x + (wasmGeometry ? 8 : -8);
		const clientY = anchor.y + (wasmGeometry ? 8 : -8);

		showPopup({
			clientX,
			clientY,
			contentBuilder: () => setImportPopupContent(customId, title, description),
			mode: "marker",
		});
	}
}

export function getPopupPoiOffset(posX: number, posY: number) {
	return {
		x: posX - 4,
		y: posY + 12,
	};
}

export async function popupPOI(data: TWasmMapHover, wasm: Wasm) {
	let anchorX = 0;
	let anchorY = 0;

	if (data.poi.geomType === "Polygon") {
		anchorX = data.clientX;
		anchorY = data.clientY;
	} else {
		const anchorData = await wasm.coordsToPixel({
			x: data.poi.position.lon,
			y: data.poi.position.lat,
		});

		anchorX = anchorData.x;
		anchorY = anchorData.y;
	}

	const { x: clientX, y: clientY } = getPopupPoiOffset(anchorX, anchorY);

	showPopup({
		clientX,
		clientY,
		contentBuilder: () => setPOIPopupContent(data.poi.source, data.poi.id, false),
		mode: "marker",
	});
}
